<script setup>
import { computed, inject, onMounted, ref, watch } from 'vue';
import { useStore } from 'vuex';
import { virtualPageview } from '@/ui/scripts/analytics/analytics.methods';
import {
  useFeatureSelect,
  useMapMarker,
  useMapProjection,
  useVectorLayers,
} from '@/app/composables';
import { Feature, Map, View } from 'ol';
import { Tile as TileLayer } from 'ol/layer';
import { OSM } from 'ol/source';
import { MAC } from 'ol/has';
import { click, platformModifierKeyOnly, touchOnly } from 'ol/events/condition';
import { DragPan, MouseWheelZoom, Select, defaults } from 'ol/interaction';
import { Point } from 'ol/geom';

let map = null;

const scotlandCenterCoords = [266866.0, 771615.51];
const config = inject('config');
const proj27700 = useMapProjection();
const { vectorLayers } = useVectorLayers(config.vectorLayers);
const markerLayer = useMapMarker();

const { selectHandler, setSelectedStyle } = useFeatureSelect();
const selectInteraction = new Select({
  condition: click,
  layers: vectorLayers.filter((layer) => layer.getProperties().selectable),
  style: (feature) => setSelectedStyle(feature),
});
const store = useStore();
const hasSelectedFeature = computed(() => store.getters['projectsMap/hasSelectedFeature']);
const locationCoordinates = computed(() => store.state.projectsMap.locationCoordinates);
const scrollOverlayVisible = ref(false);
const overlayTimeout = ref(null);
const platformModifierKey = ref('');
const lastSearchedValue = computed(() => store.state.projectsMap.lastSearchedValue);

defineProps({
  inert: {
    type: Boolean,
    required: true,
  },
});

watch(hasSelectedFeature, (value) => {
  if (value === false) {
    selectInteraction.getFeatures().clear();
  }
});

watch(locationCoordinates, (value) => {
  removeMarker();
  updateVectorLayers();
  if (value !== null) {
    addMarker(value);
  } else {
    resetCenter();
  }
});

function addMarker(value) {
  const marker = new Feature({
    geometry: new Point([value.x, value.y]),
  });
  const markerSource = markerLayer.getSource();
  markerSource.addFeature(marker);
  map.getView().setCenter([value.x, value.y]);
  map.getView().setZoom(7);
}

function checkForProjectId() {
  const urlParams = new URLSearchParams(window.location.search);
  const projectId = urlParams.get('projectId');
  return projectId !== null && projectId !== '';
}

function findExtent() {
  const layer = vectorLayers.find((vl) => vl.getSource().getFeatures().length === 1);
  return layer.getSource().getExtent();
}

function instantiateMap() {
  return new Map({
    interactions: defaults({ dragPan: false, mouseWheelZoom: false }).extend([
      new DragPan({
        condition: function (event) {
          if (touchOnly(event) === true && event.pointerType === 'touch') {
            showScrollOverlay(true);
          }
          return this.getPointerCount() === 2 || touchOnly(event) === false;
        },
      }),
      new MouseWheelZoom({
        condition: function (event) {
          if (platformModifierKeyOnly(event) === false && event.type === 'wheel') {
            showScrollOverlay(false);
          }
          return platformModifierKeyOnly(event);
        },
      }),
    ]),
    layers: [
      new TileLayer({
        source: new OSM(),
      }),
      ...vectorLayers,
      markerLayer,
    ],
    target: 'mapCanvas',
    view: new View({
      projection: proj27700,
      center: scotlandCenterCoords,
      maxZoom: 10,
      zoom: 3,
      extent: proj27700.getExtent(),
    }),
  });
}

function removeMarker() {
  const markerSource = markerLayer.getSource();
  markerSource.clear();
}

function resetCenter() {
  map.getView().setCenter(scotlandCenterCoords);
  map.getView().setZoom(3);
}

function showScrollOverlay(isTouch) {
  if (isTouch) {
    platformModifierKey.value = 'two fingers';
  } else {
    platformModifierKey.value = MAC ? '⌘' : 'Ctrl';
  }
  scrollOverlayVisible.value = true;
  clearTimeout(overlayTimeout.value);
  overlayTimeout.value = setTimeout(() => {
    scrollOverlayVisible.value = false;
  }, 1000);
}

function updateMapSize() {
  map.updateSize();
}

function updateVectorLayers() {
  const promises = vectorLayers.map((layer) => {
    return new Promise((resolve) => {
      const src = layer.getSource();
      src.once('featuresloadend', () => resolve(src.getFeatures().length));
      src.refresh();
    });
  });

  Promise.all(promises).then((values) => {
    const total = values.reduce((previousValue, currentValue) => previousValue + currentValue);
    store.commit(
      'projectsMap/SET_LAYER_ERROR',
      total > 0 ? '' : `${config.noResultsMessage} <i>"${lastSearchedValue.value}"</i>`,
    );

    if (total === 0) {
      virtualPageview(
        `/virtual/project-no-result-found/${lastSearchedValue.value}`,
        `Virtual - Project No Result Found - ${lastSearchedValue.value}`,
      );
    }
  });
}

onMounted(() => {
  // Assign map instance
  map = instantiateMap();

  // Add interactions
  map.addInteraction(selectInteraction);

  // Add event listeners
  selectInteraction.on('select', (event) =>
    selectHandler(event, map, config.projectSearchFormAction),
  );

  map.once('loadend', () => {
    //  Update vector layer params, IF there are coordinates
    if (locationCoordinates.value) {
      updateVectorLayers();
    }
    // Zoom to feature, IF query string in URL
    if (checkForProjectId()) {
      map.getView().fit(findExtent());
    }
  });
});

defineExpose({
  resetCenter,
  updateMapSize,
});
</script>

<template>
  <div
    :inert="inert"
    class="c-map-canvas"
    id="mapCanvas"
    ref="mapCanvas"
    tabindex="0"
    role="img"
    aria-label="Interactive map"
  >
    <Transition name="c-map-canvas__panel-">
      <div v-if="scrollOverlayVisible" class="c-map-canvas__overlay">
        Use {{ platformModifierKey }} + scroll to zoom the map
      </div>
    </Transition>
  </div>
</template>

<style lang="scss">
@import 'sass-mq';
@import '@/ui/styles/functions/functions';
@import '@/ui/styles/mixins/fonts';
@import '@/ui/styles/mixins/map-panels';
@import '@/ui/styles/settings/breakpoints';
@import '@/ui/styles/settings/colors';
@import '@/ui/styles/settings/fonts';
@import '@/ui/styles/settings/units';
@import 'ol/ol.css';

.ol-zoom {
  // * Overwriting default position of zoom controls
  top: auto;
  bottom: rem(28px);
  left: rem(34px);
}

.c-map-canvas {
  @include map-height;
  position: relative;
  height: 100vh;
  width: 100%;

  &.is-active {
    @include mq($until: large) {
      height: rem(300px);
      transition: height 450ms cubic-bezier(0.45, 0, 0.55, 1);
    }
  }

  &[inert]:after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    z-index: 2;
    height: 100%;
    width: 100%;
    background: rgba($white, 0.75);
  }
}

.c-map-canvas__panel--leave-active,
.c-map-canvas__panel--enter-active {
  transition: opacity 450ms cubic-bezier(0.45, 0, 0.55, 1);
}

.c-map-canvas__panel--enter-from,
.c-map-canvas__panel--leave-to {
  opacity: 0;
}

.c-map-canvas__overlay {
  @include h2-style;
  position: absolute;
  top: 0;
  left: 0;
  z-index: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: $spacing-medium;
  height: 100%;
  width: 100%;
  background: rgba($black, 0.5);
  color: $white;
  text-align: center;
  pointer-events: none;
}
</style>
